import { WorklistValues } from "features/WorklistTools/shared/interfaces";
import { is96WPlate } from "features/WorklistTools/shared/PlateHelpers";
import { isStringifiedInteger } from "features/WorklistTools/shared/WorklistHelpers";
import { useAppReduxDispatch, useAppReduxSelector } from "hooks/reduxHooks";
import { useEffect } from "react";
import { TextEditor } from "react-data-grid";
import { Alert, AlertType } from "shared-components/toast";
import { DestTransferStrategy } from "../../NucleofectionInterfaces";
import {
  anyPlatesNot96Well,
  getIntTotalVol,
  getIntVolRemaining,
} from "../../NucleofectionToolHelpers";
import { NucleofectionToolActions } from "../../state";
import { useNucleofectionStateProvider } from "../../state/StateProvider";
import { WellMapping } from "../WellMapping";
import { WellMappingTableHeader } from "../WellMappingTableHeader";

export const DestinationWellMapping = () => {
  const destPlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.destPlateInfo
  );

  const nucPlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.nucPlateInfo
  );

  const worklistValues = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.worklistValues
  );

  const { nucleofectionInternalState } = useNucleofectionStateProvider();
  const methodSettings = nucleofectionInternalState.methodSettings;
  const dispatch = useAppReduxDispatch();
  const reduxState = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present
  );
  const intTotalVol = getIntTotalVol(
    nucPlateInfo[0].topUpVol,
    nucPlateInfo[0].washVol,
    nucPlateInfo[0].numberOfWashes
  );

  const columns = [
    { key: "sourceWellId", name: "Int Pos", resizable: true, width: 75 },
    {
      key: "volremaining",
      name: "Int Vol Remaining",
      cellClass: (row: any) => {
        const srcWellId = row.sourceWellId;
        const volRemaining = getIntVolRemaining(
          srcWellId, //intWellId
          intTotalVol,
          worklistValues.intToDest
        );
        return volRemaining < 0 ? "negative-cell" : undefined;
      },
      formatter(props: any) {
        const srcWellId = props.row.sourceWellId;
        const volRemaining = getIntVolRemaining(
          srcWellId, //intWellId
          intTotalVol,
          worklistValues.intToDest
        );
        const tooMuchVolUsed = volRemaining < 0;

        return (
          <span
            style={{
              color: tooMuchVolUsed ? "red" : "inherit",
            }}
          >
            {volRemaining}
          </span>
        );
      },
      resizable: true,
      editable: false,
    },
    {
      key: "destPlateIndex",
      name: "Dest Plate",
      formatter(props: any) {
        return <>{props.row.destPlateIndex + 1}</>;
      },
      resizable: true,
      width: 100,
    },
    { key: "destWellId", name: "Dest Pos", resizable: true, width: 85 },
    {
      key: "transferVol",
      name: "Transfer Vol",
      resizable: true,
      editor: TextEditor,
      editable: (row: any) => {
        const isEditable = !is96WPlate(
          destPlateInfo[row.destPlateIndex].rows.length,
          destPlateInfo[row.destPlateIndex].cols.length
        );
        return isEditable;
      },
      formatter({ row }: any) {
        const value = row.transferVol ?? "";
        const parsed = parseInt(value, 10);
        return <>{!isNaN(parsed) ? parsed.toLocaleString("en-US") : value}</>;
      },
    },
  ];

  const handleApplyVolume = (
    option: string | number,
    transferVol: number | undefined
  ) => {
    if (!transferVol) {
      return Alert({
        type: AlertType.ERROR,
        message: "Please enter a number to apply",
      });
    }
    switch (option) {
      case "Apply to All":
        dispatch(
          NucleofectionToolActions.UPDATE_ALL_DEST_WELLS_TRANSFERVOL({
            transferVol: transferVol.toString(),
          })
        );
        break;
      case "Apply to Empty":
        dispatch(
          NucleofectionToolActions.UPDATE_EMPTY_DEST_WELLS_TRANSFERVOL({
            transferVol: transferVol.toString(),
          })
        );
        break;
      default:
        dispatch(
          NucleofectionToolActions.UPDATE_WELLS_IN_SPECIFIC_DESTPLATE_TRANSFERVOL(
            {
              transferVol: transferVol.toString(),
              destPlateIndex: option,
            }
          )
        );
        break;
    }
  };

  useEffect(() => {
    const destPlatesWithData = destPlateInfo.filter(
      (destPlate) => destPlate.labwareTypeCode !== ""
    );
    if (
      anyPlatesNot96Well(destPlateInfo) ||
      methodSettings.destTransferStrategy === DestTransferStrategy.Custom ||
      destPlatesWithData.length === 0
    ) {
      return;
    }

    if (
      methodSettings.destTransferStrategy === DestTransferStrategy.SplitEvenly
    ) {
      const minRemainingDestCapacity = Math.min(
        ...destPlatesWithData.map(
          (destPlate) => destPlate.operatingVol - destPlate.minOperatingVol
        )
      );
      const num96WPlates = destPlatesWithData.length;
      let splitEvenlyVol = Math.floor(intTotalVol / num96WPlates);

      if (splitEvenlyVol > minRemainingDestCapacity) {
        splitEvenlyVol = minRemainingDestCapacity;
      }

      handleApplyVolume("Apply to All", splitEvenlyVol);
    } else if (
      methodSettings.destTransferStrategy ===
      DestTransferStrategy.MaximizeVolume
    ) {
      type DestPlateVolDict = Record<number, string>;
      const destPlateVolDict: DestPlateVolDict = {};
      let intVolRemaining = intTotalVol;

      for (const destPlate of destPlatesWithData) {
        const destCapacity = destPlate.operatingVol - destPlate.minOperatingVol;
        let transferVol = 0;
        if (destCapacity >= intVolRemaining) {
          transferVol = intVolRemaining;
        } else {
          transferVol = destCapacity;
        }
        destPlateVolDict[destPlate.index] =
          transferVol === 0 ? "" : transferVol.toString();
        intVolRemaining -= transferVol;
      }
      dispatch(
        NucleofectionToolActions.UPDATE_MULTIPLE_DESTPLATE_TRANSFERVOL({
          destPlateVolDict,
        })
      );
    }
  }, [destPlateInfo, methodSettings.destTransferStrategy, intTotalVol]);

  const setSingleCellNumber = (rows: unknown[]) => {
    const typedRows = rows as WorklistValues[];

    for (const row of typedRows) {
      if (
        row.transferVol !== "" &&
        row.transferVol &&
        !isStringifiedInteger(row.transferVol)
      ) {
        return Alert({
          type: AlertType.ERROR,
          message: "Please enter a valid whole number",
        });
      }
    }
    dispatch(
      NucleofectionToolActions.UPDATE_SINGLE_DEST_WELL_TRANSFERVOL({
        rows,
      })
    );
  };

  return (
    <WellMapping
      setSingleCellNumber={setSingleCellNumber}
      columns={columns}
      worklistValues={worklistValues.intToDest}
      tableHeader={
        methodSettings.destTransferStrategy === DestTransferStrategy.Custom ? (
          <WellMappingTableHeader
            handleApplyCellNumber={handleApplyVolume}
            destPlates={reduxState.destPlateInfo}
          />
        ) : null
      }
    />
  );
};
